import type { Placement, Point, PositionAndPlacement } from "./types";

const mouseWidth = 10;
const mouseHeight = 16;

/**
 * Determines the tooltip's position and placement.
 * @param trigger
 * @param tooltip
 * @param arrow
 * @param placement
 * @param offset
 */
export function determineTooltipPositionAndPlacement(
  trigger: HTMLElement,
  tooltip: HTMLElement,
  arrow: HTMLElement | null,
  placement: Placement,
  offsetArray?: number[],
): PositionAndPlacement {
  // Get bounding rects
  const triggerRect = trigger.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const arrowRect = (arrow && arrow.getBoundingClientRect()) || null;

  // Convert offset array to Point
  const offset = arrayToPoint(offsetArray);

  // Calculate the initial tooltip position
  let position = calculateTooltipPosition(
    triggerRect,
    tooltipRect,
    arrowRect,
    placement,
    offset,
  );

  // Check if the tooltip needs to be flipped to stay within the viewport
  if (isTooltipFlipNeeded(tooltipRect, placement, position)) {
    // Flip placement and recalculate position
    placement = getFlippedPlacement(placement);
    position = calculateTooltipPosition(
      triggerRect,
      tooltipRect,
      arrowRect,
      placement,
      offset,
    );
  }

  // Ensure the tooltip's position is within the viewport
  position = clampTooltipPositionWithinViewport(tooltipRect, placement, position);

  return { position, placement };
}

/**
 * Determines the tooltip's position from mouse position.
 * @param trigger
 * @param tooltip
 * @param arrow
 * @param placement
 * @param offset
 */
export function determineTooltipPositionFromMousePosition(
  tooltip: HTMLElement,
  mouse: Point,
  offsetArray?: number[],
): Point {
  // Get viewport and bounding rects
  const { x: maxWidth, y: maxHeight } = getViewport();
  const tooltipRect = tooltip.getBoundingClientRect();

  // Convert offset array to Point
  const offset = arrayToPoint(offsetArray);

  // Check best x
  let x = mouse.x + mouseWidth + offset.x;
  if (x + tooltipRect.width > maxWidth) {
    const testX = mouse.x - offset.x - tooltipRect.width;
    if (testX > 0) x = testX;
  }

  let y = mouse.y + mouseHeight + offset.y;
  if (y + tooltipRect.height > maxHeight) {
    const testY = mouse.y - offset.y - tooltipRect.height;
    if (testY > 0) y = testY;
  }

  return { x, y };
}

/**
 * Calculates the position of the tooltip based on the trigger element, tooltip dimensions, placement, arrow presence, and offset.
 * @param triggerRect
 * @param tooltipRect
 * @param arrowRect
 * @param placement
 * @param offset
 */
function calculateTooltipPosition(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  arrowRect: DOMRect | null,
  placement: Placement,
  offset: Point,
): Point {
  const anchor = getTooltipAnchorPoint(triggerRect, placement);
  const alignment = getTooltipAlignment(placement);

  // Calculate the initial x and y positions for the tooltip
  let x = anchor.x + alignment.x * tooltipRect.width + offset.x;
  let y = anchor.y + alignment.y * tooltipRect.height - offset.y;

  // If the tooltip has an arrow, adjust the position to account for the arrow's offset
  if (arrowRect) {
    const arrowOffset = getTooltipArrowOffset(arrowRect, placement);
    x += arrowOffset.x;
    y += arrowOffset.y;
  }

  return { x, y };
}

/**
 * Returns the anchor point for the tooltip based on the trigger element's dimensions and the specified placement.
 * @param triggerRect
 * @param placement
 */
function getTooltipAnchorPoint(triggerRect: DOMRect, placement: Placement): Point {
  let x;
  switch (placement) {
    case "top":
    case "bottom":
      // centered on the trigger element
      x = triggerRect.left + triggerRect.width / 2;
      break;
    case "top-start":
    case "bottom-start":
    case "left":
    case "left-start":
    case "left-end":
      // at the left edge of the trigger element
      x = triggerRect.left;
      break;
    case "top-end":
    case "bottom-end":
    case "right":
    case "right-start":
    case "right-end":
      // at the right edge of the trigger element
      x = triggerRect.right;
      break;
  }

  let y;
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
    case "left-start":
    case "right-start":
      // at the top edge of the trigger element
      y = triggerRect.top;
      break;
    case "bottom":
    case "bottom-start":
    case "bottom-end":
    case "left-end":
    case "right-end":
      // at the bottom edge of the trigger element
      y = triggerRect.bottom;
      break;
    case "left":
    case "right":
      // centered on the trigger element
      y = triggerRect.top + triggerRect.height / 2;
      break;
  }

  return { x, y };
}

/**
 * Returns the alignment in percentage based on the tooltip's placement.
 * @param placement
 */
function getTooltipAlignment(placement: Placement): Point {
  let x;
  switch (placement) {
    case "top":
    case "bottom":
      // centered horizontally
      x = -0.5;
      break;
    case "top-start":
    case "bottom-start":
    case "right":
    case "right-start":
    case "right-end":
      // aligns with the left side
      x = 0;
      break;
    case "top-end":
    case "bottom-end":
    case "left":
    case "left-start":
    case "left-end":
      // aligns with the right side
      x = -1.0;
      break;
  }

  let y;
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
    case "left-end":
    case "right-end":
      // aligns with the top side
      y = -1.0;
      break;
    case "bottom":
    case "bottom-start":
    case "bottom-end":
    case "left-start":
    case "right-start":
      // aligns with the bottom side
      y = 0;
      break;
    case "left":
    case "right":
      // centered vertically
      y = -0.5;
      break;
  }

  return { x, y };
}

/**
 * Returns the absolute offset needed for the arrow.
 * @param arrowRect
 * @param placement
 */
function getTooltipArrowOffset(arrowRect: DOMRect, placement: Placement): Point {
  let x;
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      // centered
      x = 0;
      break;
    case "left":
    case "left-start":
    case "left-end":
      // positioned to the left
      x = -1;
      break;
    case "right":
    case "right-start":
    case "right-end":
      // positioned to the right
      x = 1;
      break;
  }

  let y;
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
      // positioned above
      y = -1;
      break;
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      // positioned below
      y = 1;
      break;
    case "left":
    case "left-start":
    case "left-end":
    case "right":
    case "right-start":
    case "right-end":
      // centered
      y = 0;
      break;
  }

  return { x: x * arrowRect.width * 0.7, y: y * arrowRect.height * 0.7 };
}

/**
 * Checks if the tooltip's placement needs to be flipped to keep it within the viewport.
 * @param tooltipRect
 * @param placement
 * @param position
 */
function isTooltipFlipNeeded(
  tooltipRect: DOMRect,
  placement: Placement,
  position: Point,
): boolean {
  const { x: maxWidth, y: maxHeight } = getViewport();

  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
      return position.y < 0;
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      return position.y + tooltipRect.height > maxHeight;
    case "left":
    case "left-start":
    case "left-end":
      return position.x < 0;
    case "right":
    case "right-start":
    case "right-end":
      return position.x + tooltipRect.width > maxWidth;
  }
}

/**
 * Adjusts the tooltip's position to ensure it stays fully within the viewport.
 * @param tooltipRect
 * @param placement
 * @param position
 */
function clampTooltipPositionWithinViewport(
  tooltipRect: DOMRect,
  placement: Placement,
  position: Point,
): Point {
  const { x: maxWidth, y: maxHeight } = getViewport();

  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      // adjust the horizontal position
      position.x = clamp(position.x, 0, maxWidth - tooltipRect.width);
      break;
    case "left":
    case "left-start":
    case "left-end":
    case "right":
    case "right-start":
    case "right-end":
      // adjust the vertical  position
      position.y = clamp(position.y, 0, maxHeight - tooltipRect.height);
  }

  return position;
}

/**
 * TODO: Calculates the position of the arrow within the tooltip, ensuring it is properly aligned and visible.
 * @param trigger
 * @param tooltip
 * @param arrow
 * @param placement
 * @param position
 */
export function calculateArrowPosition(
  trigger: HTMLElement,
  tooltip: HTMLElement,
  arrow: HTMLElement,
  placement: Placement,
  position: Point,
): Point {
  // Get bounding rects
  const triggerRect = trigger.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const arrowRect = (arrow && arrow.getBoundingClientRect()) || null;
  const tooltipInnerSize: Point = { x: tooltip.clientWidth, y: tooltip.clientHeight };

  // Calculate the initial x and y positions for the arrow
  const arrowPosition = getArrowAnchorPoint(
    triggerRect,
    tooltipRect,
    placement,
    position,
  );

  // Adjust position if tooltip has a border
  arrowPosition.x -= (tooltipRect.width - tooltipInnerSize.x) / 2;
  arrowPosition.y -= (tooltipRect.height - tooltipInnerSize.y) / 2;

  // Center the arrow
  arrowPosition.x -= arrowRect.width / 2;
  arrowPosition.y -= arrowRect.height / 2;

  return clampArrowWithinTooltip(tooltipRect, arrowRect, placement, arrowPosition);
}

/**
 * TODO: Returns the anchor point of the arrow within the tooltip based on the placement and position.
 * @param triggerRect
 * @param tooltipRect
 * @param placement
 * @param position
 */
function getArrowAnchorPoint(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  placement: Placement,
  position: Point,
): Point {
  let x, y;
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
      // at the bottom of the tooltip
      x = triggerRect.left - position.x + triggerRect.width / 2;
      y = tooltipRect.height;
      break;
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      // at the top of the tooltip
      y = 0;
      x = triggerRect.left - position.x + triggerRect.width / 2;
      break;
    case "left":
    case "left-start":
    case "left-end":
      // at the right of the tooltip
      x = tooltipRect.width;
      y = triggerRect.top - position.y + triggerRect.height / 2;
      break;
    case "right":
    case "right-start":
    case "right-end":
      // at the left of the tooltip
      x = 0;
      y = triggerRect.top - position.y + triggerRect.height / 2;
      break;
  }

  return { x, y };
}

/**
 * Adjusts the position of the arrow to ensure it stays within the boundaries of the tooltip.
 * @param tooltipRect
 * @param arrowRect
 * @param placement
 * @param position
 */
function clampArrowWithinTooltip(
  tooltipRect: DOMRect,
  arrowRect: DOMRect,
  placement: Placement,
  position: Point,
): Point {
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      // adjust the horizontal position
      position.x = clamp(
        position.x,
        arrowRect.width / 2,
        tooltipRect.width - arrowRect.width * 1.5,
      );
      break;
    case "left":
    case "left-start":
    case "left-end":
    case "right":
    case "right-start":
    case "right-end":
      // adjust the vertical position
      position.y = clamp(
        position.y,
        arrowRect.height / 2,
        tooltipRect.height - arrowRect.height * 1.5,
      );
      break;
  }

  return position;
}

/**
 * Returns the opposite placement value for the given tooltip placement.
 * @param placement
 */
function getFlippedPlacement(placement: Placement): Placement {
  switch (placement) {
    case "top":
      return "bottom";
    case "top-start":
      return "bottom-start";
    case "top-end":
      return "bottom-end";
    case "bottom":
      return "top";
    case "bottom-start":
      return "top-start";
    case "bottom-end":
      return "top-end";
    case "left":
      return "right";
    case "left-start":
      return "right-start";
    case "left-end":
      return "right-end";
    case "right":
      return "left";
    case "right-start":
      return "left-start";
    case "right-end":
      return "left-end";
  }
}

/**
 * Returns the viewport
 * @returns
 */
function getViewport(): Point {
  return {
    x: document.documentElement.clientWidth,
    y: document.documentElement.clientHeight,
  };
}

/**
 * Converts an array of to two numbers into a Point object.
 * @param arr
 */
function arrayToPoint(arr: number[] = []): Point {
  const [x = 0, y = 0] = arr;
  return { x, y };
}

/**
 * Restricts a value to a specified range between min and max.
 * @param value
 * @param min
 * @param max
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}
